import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getSurveys, createSurvey, respondToSurvey, getSurveyResponses, deleteSurvey, getRoles } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Plus, ClipboardList, Trash2, Eye, Check, X } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

export default function Surveys() {
  const { user, hasPermission } = useAuth();
  const [surveys, setSurveys] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showRespond, setShowRespond] = useState(null);
  const [showResults, setShowResults] = useState(null);
  const [results, setResults] = useState([]);
  const [answers, setAnswers] = useState({});
  const [surveyForm, setSurveyForm] = useState({
    title: '',
    description: '',
    questions: [{ text: '', type: 'text', options: [] }],
    target_roles: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [surveysRes, rolesRes] = await Promise.all([getSurveys(), getRoles()]);
      setSurveys(surveysRes.data);
      setRoles(rolesRes.data);
    } catch (error) {
      toast.error('Error al cargar encuestas');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSurvey = async () => {
    if (!surveyForm.title || surveyForm.questions.length === 0) {
      toast.error('Completa los campos requeridos');
      return;
    }
    try {
      await createSurvey(surveyForm);
      toast.success('Encuesta creada');
      setShowCreate(false);
      setSurveyForm({
        title: '',
        description: '',
        questions: [{ text: '', type: 'text', options: [] }],
        target_roles: []
      });
      fetchData();
    } catch (error) {
      toast.error('Error al crear encuesta');
    }
  };

  const handleRespond = async () => {
    if (!showRespond) return;
    
    const answersArray = Object.entries(answers).map(([questionIndex, answer]) => ({
      question_index: parseInt(questionIndex),
      answer
    }));

    try {
      await respondToSurvey(showRespond.id, { survey_id: showRespond.id, answers: answersArray });
      toast.success('Respuesta enviada');
      setShowRespond(null);
      setAnswers({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al responder');
    }
  };

  const handleViewResults = async (survey) => {
    try {
      const response = await getSurveyResponses(survey.id);
      setResults(response.data);
      setShowResults(survey);
    } catch (error) {
      toast.error('Error al cargar resultados');
    }
  };

  const handleDeleteSurvey = async (id) => {
    if (!window.confirm('¿Eliminar esta encuesta?')) return;
    try {
      await deleteSurvey(id);
      toast.success('Encuesta eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar encuesta');
    }
  };

  const addQuestion = () => {
    setSurveyForm({
      ...surveyForm,
      questions: [...surveyForm.questions, { text: '', type: 'text', options: [] }]
    });
  };

  const removeQuestion = (index) => {
    setSurveyForm({
      ...surveyForm,
      questions: surveyForm.questions.filter((_, i) => i !== index)
    });
  };

  const updateQuestion = (index, field, value) => {
    const newQuestions = [...surveyForm.questions];
    newQuestions[index] = { ...newQuestions[index], [field]: value };
    setSurveyForm({ ...surveyForm, questions: newQuestions });
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), "d 'de' MMMM, yyyy", { locale: es });
    } catch {
      return dateStr;
    }
  };

  const canCreate = hasPermission('encuestas', 'crear');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="surveys-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-emerald-900 font-['Outfit']">
            Encuestas y Formularios
          </h1>
          <p className="text-muted-foreground">
            Recopila información de los miembros
          </p>
        </div>
        {canCreate && (
          <Button onClick={() => setShowCreate(true)} data-testid="create-survey-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Encuesta
          </Button>
        )}
      </div>

      <div className="grid gap-4">
        {surveys.length > 0 ? (
          surveys.map((survey) => (
            <Card key={survey.id} className="card-hover">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-purple-100">
                      <ClipboardList className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{survey.title}</CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        {survey.questions?.length || 0} preguntas • {formatDate(survey.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setShowRespond(survey); setAnswers({}); }}
                      data-testid={`respond-survey-${survey.id}`}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Responder
                    </Button>
                    {canCreate && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewResults(survey)}
                          data-testid={`view-results-${survey.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Resultados
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDeleteSurvey(survey.id)}
                          data-testid={`delete-survey-${survey.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              {survey.description && (
                <CardContent>
                  <p className="text-stone-600">{survey.description}</p>
                </CardContent>
              )}
            </Card>
          ))
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <ClipboardList className="w-12 h-12 mx-auto mb-4 text-stone-300" />
              <p className="text-muted-foreground">No hay encuestas disponibles</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Survey Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nueva Encuesta</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Título *</Label>
              <Input
                value={surveyForm.title}
                onChange={(e) => setSurveyForm({ ...surveyForm, title: e.target.value })}
                placeholder="Título de la encuesta"
                data-testid="survey-title"
              />
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea
                value={surveyForm.description}
                onChange={(e) => setSurveyForm({ ...surveyForm, description: e.target.value })}
                placeholder="Descripción de la encuesta..."
                rows={2}
                data-testid="survey-description"
              />
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Preguntas</Label>
                <Button type="button" variant="outline" size="sm" onClick={addQuestion}>
                  <Plus className="w-4 h-4 mr-1" />
                  Agregar Pregunta
                </Button>
              </div>
              
              {surveyForm.questions.map((question, index) => (
                <div key={index} className="p-4 rounded-lg bg-stone-50 space-y-3">
                  <div className="flex items-start justify-between">
                    <Label className="text-sm">Pregunta {index + 1}</Label>
                    {surveyForm.questions.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => removeQuestion(index)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <Input
                    value={question.text}
                    onChange={(e) => updateQuestion(index, 'text', e.target.value)}
                    placeholder="Escribe la pregunta..."
                    data-testid={`question-${index}`}
                  />
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateSurvey} data-testid="submit-survey-btn">
              Crear Encuesta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Respond Survey Dialog */}
      <Dialog open={!!showRespond} onOpenChange={() => setShowRespond(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{showRespond?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {showRespond?.description && (
              <p className="text-muted-foreground">{showRespond.description}</p>
            )}
            {showRespond?.questions?.map((question, index) => (
              <div key={index} className="space-y-2">
                <Label>{question.text}</Label>
                <Textarea
                  value={answers[index] || ''}
                  onChange={(e) => setAnswers({ ...answers, [index]: e.target.value })}
                  placeholder="Tu respuesta..."
                  rows={2}
                  data-testid={`answer-${index}`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRespond(null)}>
              Cancelar
            </Button>
            <Button onClick={handleRespond} data-testid="submit-response-btn">
              Enviar Respuesta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Results Dialog */}
      <Dialog open={!!showResults} onOpenChange={() => setShowResults(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Resultados: {showResults?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-muted-foreground">
              Total de respuestas: {results.length}
            </p>
            {results.length > 0 ? (
              results.map((response, idx) => (
                <div key={response.id || idx} className="p-4 rounded-lg bg-stone-50 space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Respuesta #{idx + 1} • {formatDate(response.created_at)}
                  </p>
                  {response.answers?.map((answer, ansIdx) => (
                    <div key={ansIdx}>
                      <p className="text-sm font-medium">
                        {showResults?.questions?.[answer.question_index]?.text || `Pregunta ${answer.question_index + 1}`}
                      </p>
                      <p className="text-stone-600">{answer.answer}</p>
                    </div>
                  ))}
                </div>
              ))
            ) : (
              <p className="text-center text-muted-foreground py-8">
                No hay respuestas todavía
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResults(null)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
